import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { Modal, ModalProps } from "features";
import { Button, InputStyledDiv, Input, Loading, Textarea } from "components";
import { EndUser, EndUserUpdateData, useUpdateEndUserMutation } from "services";
import { useUserManagementContext } from "../utils";
import { Message } from "features/Modal";
import { t } from "i18next";

type ModalStep = "initial" | "success" | "error";

interface UserEditModalProps extends ModalProps {
  user: EndUser | null;
}

export const UserEditModal = ({ open, setOpen, user }: UserEditModalProps) => {
  const [step, setStep] = useState<ModalStep>("initial");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [formData, setFormData] = useState<EndUserUpdateData>({
    name: "",
    first_name: "",
    family_name: "",
    phone_number: "",
    city_id: 0,
    language: "",
    enabled: true,
    credits: "",
  });

  const [updateEndUser, updateResult] = useUpdateEndUserMutation();
  const { refetch } = useUserManagementContext();

  // Initialize form data when user changes
  useEffect(() => {
    if (user && open) {
      setFormData({
        name: user.name,
        first_name: user.first_name,
        family_name: user.family_name,
        phone_number: user.phone_number,
        city_id: user.city?.id || 0,
        language: user.language,
        enabled: user.enabled,
        credits: user.credits,
      });
      setStep("initial");
      setErrorMessage("");
    }
  }, [user, open]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (event.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!user) return;

    try {
      await updateEndUser({
        username: user.uuid,
        userData: formData,
      }).unwrap();

      // Refresh the data
      await refetch();
      setStep("success");
    } catch (error: any) {
      const errorMsg = error?.data?.apierror || error?.message || t("userManagement.userUpdateFailed");
      setErrorMessage(errorMsg);
      setStep("error");
    }
  };

  const handleClose = () => {
    setOpen(false);
    setStep("initial");
    setErrorMessage("");
  };

  if (!user) return null;

  return (
    <Modal withActions={false} withClose={step === "initial" || step === "error"} open={open} setOpen={handleClose}>
      {step === "initial" && (
        <div className="relative flex flex-col gap-y-6">
          <h2 className="text-xl font-semibold">{t("userManagement.editUser")}</h2>

          <form onSubmit={handleSubmit} className="flex flex-col gap-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium">{t("common.firstName")}*</label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t("common.firstName").toLowerCase()}
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">{t("common.lastName")}*</label>
              <Input
                name="family_name"
                value={formData.family_name}
                onChange={handleInputChange}
                placeholder={t("common.lastName").toLowerCase()}
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">{t("common.phone")}*</label>
              <Input
                name="phone_number"
                type="tel"
                value={formData.phone_number}
                onChange={handleInputChange}
                placeholder={t("common.phone").toLowerCase()}
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">{t("common.city")}</label>
              <Input
                name="city_id"
                type="number"
                value={formData.city_id.toString()}
                onChange={handleInputChange}
                placeholder={t("common.city").toLowerCase()}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">{t("common.language")}</label>
              <Input
                name="language"
                value={formData.language}
                onChange={handleInputChange}
                placeholder={t("common.language").toLowerCase()}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">{t("userManagement.credits")}</label>
              <Input
                name="credits"
                value={formData.credits}
                onChange={handleInputChange}
                placeholder={t("userManagement.credits").toLowerCase()}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="enabled"
                name="enabled"
                checked={formData.enabled}
                onChange={handleInputChange}
                className="rounded"
              />
              <label htmlFor="enabled" className="text-sm font-medium">
                {t("common.enabled")}
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button type="button" variant="bg-primary/50" onClick={handleClose}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={updateResult.isLoading}>
                {t("common.save")}
              </Button>
            </div>
          </form>

          {updateResult.isLoading && <Loading absolute />}
        </div>
      )}

      {step === "success" && (
        <Message variant="success" message={t("userManagement.userUpdated")}>
          <div className="mt-2 text-center">
            <button className="text-blue-600 underline hover:text-blue-800" onClick={handleClose}>
              {t("common.continue")}
            </button>
          </div>
        </Message>
      )}

      {step === "error" && (
        <Message variant="error" message={errorMessage}>
          <div className="mt-2 text-center">
            <button className="text-red-600 underline hover:text-red-800" onClick={() => setStep("initial")}>
              {t("common.tryAgain")}
            </button>
          </div>
        </Message>
      )}
    </Modal>
  );
};
